import 'antd/dist/reset.css';
import './App.css';
import 'react-circular-progressbar/dist/styles.css';
import { Button, Layout, Menu, message, Spin, Switch } from 'antd';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { isMobileOnly } from 'react-device-detect';
import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { SyncOutlined } from '@ant-design/icons';
import Login from './pages/login';
import { authStore } from './authStore';
import repository from './repository';
import Flex from './components/flex';
import AccountDetail from './pages/accountDetail/index';
import { Interval } from './constants/interval';
import settingStore from './stores/settingStore';
import { observer } from 'mobx-react';
import ScheduleLogsModal from './modals/scheduleLogsModal';
import moment from 'moment';
import { supabase } from './supabaseClient';

const SetupAccount = lazy(() => import('./pages/setupAccount/index'));
const UnlockAccount = lazy(() => import('./pages/unlockAccount/index'));
const UpdateSession = lazy(() => import('./pages/updateSettion/index'));
const OrderStatus = lazy(() => import('./pages/orderStatus/index'));
const AccountList = lazy(() => import('./pages/accountList'));
const WorkStatus = lazy(() => import('./pages/workStatus'));
const UploadPost = lazy(() => import('./pages/uploadPost/index'));
const FreePromotion = lazy(() => import('./pages/freePromotion'));
const AsList = lazy(() => import('./pages/asList'));
const ProxyControl = lazy(() => import('./pages/proxyControl'));
const ManualOrderForm = lazy(() => import('./pages/manualOrderForm'));
const SubscribeManagement = lazy(() => import('./pages/subscribeManagement'));
const Training = lazy(() => import('./pages/training'));
const Setting = lazy(() => import('./pages/setting'));
const DM = lazy(() => import('./pages/dm'));
const CompleteOrderStatus = lazy(() => import('./pages/completeOrderStatus'));
const Membership = lazy(() => import('./pages/membership'));
const DepositManagement = lazy(() => import('./pages/deposit'));
const MappedDeposit = lazy(() => import('./pages/mappedDeposit'));
const ManualOrderAutoForm = lazy(() => import('./pages/manualOrderAutoForm'));
const FlashNotification = lazy(() => import('./pages/flashNotification'));
const Lab = lazy(() => import('./pages/lab'));
const ForeignerOrder = lazy(() => import('./pages/foreignerOrder'));
const BannedList = lazy(() => import('./pages/bannedList'));
const TrainingCenter = lazy(() => import('./pages/trainingCenter'));
const CanceledOrderStatus = lazy(() => import('./pages/canceledOrderStatus'));
const PromotionBlackList = lazy(() => import('./pages/promotionBlackList'));
const AdminSetting = lazy(() => import('./pages/adminSetting'));
const PackageManagement = lazy(() => import('./pages/packageManagement'));
const Customer = lazy(() => import('./pages/customer'));
const AccountLogs = lazy(() => import('./pages/accountLogs'));
const DeviceAppAccounts = lazy(() => import('./pages/deviceAppAccounts'));
const DeviceManagement = lazy(() => import('./pages/deviceManagement'));
const AppiumDashboard = lazy(() => import('./pages/appiumDashboard'));
const { Content, Sider } = Layout;

const pages = [
    {
        name: '로그인',
        key: 'login',
        component: <Login />,
    },
    {
        name: '계정 정보',
        key: '/account/detail/:id',
        component: <AccountDetail />
    }
];

const privatePages = [
    {
        name: '계정 관리',
        key: 'account',
        subPages: [
            {
                name: '계정 리스트',
                key: 'account-list',
                component: <AccountList />
            },
            {
                name: '벤 리스트',
                key: 'banned-list',
                component: <BannedList />
            },
            {
                name: '잠금 해제',
                key: 'unlock',
                component: <UnlockAccount />
            },
            {
                name: '쿠키 업데이트',
                key: 'update-session',
                component: <UpdateSession />
            },
            {
                name: '계정 세팅',
                key: 'setup',
                component: <SetupAccount />
            },
            {
                name: '훈련소',
                key: 'training-center',
                component: <TrainingCenter />
            },
            {
                name: '게시물 올리기',
                key: 'upload-post',
                component: <UploadPost />
            },
            {
                name: '계정 투입',
                key: 'training',
                component: <Training />
            },
        ],
    },
    {
        name: '주문 현황',
        key: 'order-status',
        component: <OrderStatus />
    },
    {
        name: '취소, 완료, 외국인 주문',
        key: 'orders',
        subPages: [
            {
                name: '취소 주문 현황',
                key: 'canceled-order-status',
                component: <CanceledOrderStatus />,
            },
            {
                name: '완료 주문 현황',
                key: 'complete-order-status',
                component: <CompleteOrderStatus />,
            },
            {
                name: '외국인 주문',
                key: 'foreigner-order',
                component: <ForeignerOrder />
            },
        ],
    },
    {
        name: '작업 현황',
        key: 'work-status',
        component: <WorkStatus />
    },
    {
        name: '노르망디',
        key: 'normandie',
        subPages: [
            {
                name: '메인',
                key: 'main',
                component: <AppiumDashboard />,
            },
            {
                name: '로그',
                key: 'app-account-logs',
                component: <AccountLogs />,
            },
            {
                name: '계정 관리',
                key: 'app-account-management',
                component: <DeviceAppAccounts />,
            },
            {
                name: '디바이스 관리',
                key: 'device-management',
                component: <DeviceManagement />,
            },
        ],
    },
    {
        name: '수동 주문',
        key: 'manual-order',
        subPages: [
            {
                name: '폼 주문',
                key: 'form',
                component: <ManualOrderForm />,
            },
            {
                name: '자동 폼 주문',
                key: 'auto-form',
                component: <ManualOrderAutoForm />,
            },
        ],
    },
    {
        name: '무료 프로모션',
        key: 'free-promotion',
        subPages: [
            {
                name: '프로모션 주문',
                key: 'promotion-order',
                component: <FreePromotion />,
            },
            {
                name: '블랙리스트',
                key: 'promotion-blacklist',
                component: <PromotionBlackList />,
            },
        ],
    },
    {
        name: '구독 관리',
        key: 'subscribe-management',
        component: <SubscribeManagement />
    },
    {
        name: '패키지 관리',
        key: 'package-management',
        component: <PackageManagement />
    },
    {
        name: '입금',
        key: 'deposit',
        subPages: [
            {
                name: '미완료 입금',
                key: 'deposit-management',
                component: <DepositManagement />
            },
            {
                name: '완료 입금 현황',
                key: 'mapped-deposit-management',
                component: <MappedDeposit />
            },
        ],
    },
    {
        name: '멤버십',
        key: 'membership',
        component: <Membership />
    },
    {
        name: 'A/S',
        key: 'as-list',
        component: <AsList />
    },
    {
        name: '고객정보',
        key: 'customer',
        component: <Customer />
    },
    {
        name: '프록시 컨트롤',
        key: 'proxy-control',
        component: <ProxyControl />
    },
    {
        name: '공지사항',
        key: 'flash-notification',
        component: <FlashNotification />
    },
    {
        name: '환경설정',
        key: 'setting',
        component: <Setting />
    },
    {
        name: '실험실',
        key: 'lab',
        component: <Lab />
    },
    {
        name: 'DM',
        key: 'dm',
        component: <DM />
    },
    {
        name: '관리자계정 설정',
        key: 'admin-setting',
        component: <AdminSetting />
    }
];

const keyMapper = {
    '/account/unlock': 'unlock',
    '/account/update-session': 'update-session',
    '/account/setup': 'setup',
    '/account/training-center': 'training-center',
    '/account/account-list': 'account-list',
    '/account/banned-list': 'banned-list',
    '/account/upload-post': 'upload-post',
    '/account/training': 'training',
    '/order-status': 'order-status',
    '/work-status': 'work-status',
    '/normandie/main': 'normandie-main',
    '/normandie/device-management': 'device-management',
    '/normandie/app-account-logs': 'app-account-logs',
    '/normandie/app-account-management': 'app-account-management',
    '/manual-order/form': 'form',
    '/manual-order/auto-form': 'auto-form',
    '/free-promotion/promotion-order': 'promotion-order',
    '/free-promotion/promotion-blacklist': 'promotion-blacklist',
    '/as-list': 'as-list',
    '/proxy-control': 'proxy-control',
    '/setting': 'setting',
    '/subscribe-management': 'subscribe-management',
    '/package-management': 'package-management',
    '/orders/canceled-order-status': 'canceled-order-status',
    '/orders/complete-order-status': 'complete-order-status',
    '/orders/foreigner-order': 'foreigner-order',
    '/membership': 'membership',
    '/deposit/deposit-management': 'deposit-management',
    '/deposit/mapped-deposit': 'mapped-deposit-management',
    '/flash-notification': 'flash-notification',
    '/lab': 'lab',
    '/admin-setting': 'admin-setting',
    '/customer': 'customer',
};

const WorkableQuota = () => {
    const [quota, setQuota] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);

    const _getWorkableQuota = async () => {
        setLoading(true);
        const quota = await repository.getWorkableQuota();
        if (typeof quota === 'number') {
            setQuota(quota);
        }
        setTimeout(() => {
            setLoading(false);
        }, 300);
    };

    useEffect(() => {
        _getWorkableQuota();
    }, []);

    useEffect(() => {
        const timerId = setInterval(() => {
            _getWorkableQuota();
        }, 10 * 1000);
        return () => clearInterval(timerId);
    }, []);

    return (
        <div>
            <div style={{ marginBottom: 4 }}>배정가능 스케줄 수</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ color: '#F77737', fontSize: 20, fontWeight: 'bold' }}>
                    {loading ? '-' : quota.toLocaleString()}
                </span>
                <button
                    onClick={_getWorkableQuota}
                    style={{
                        padding: 4,
                    }}
                >
                    <SyncOutlined
                        spin={loading}
                        style={{
                            fontSize: 14,
                        }}
                    />
                </button>
            </div>
        </div>
    );
};

type DashboardStatus = {
    remainCnts: {
        follow: number;
        like: number;
    };
    saturationRate: number;
    subscriptionFollow: {
        remain: number;
        total: number;
    };
};

const ScheduleMonitor = observer(() => {
    const [enabled, setEnabled] = useState<boolean>();
    const [loading, setLoading] = useState<boolean>(false);
    const [dashboardStatus, setDashboardStatus] = useState<DashboardStatus>();
    const [scheduleLogsModalOpen, setScheduleLogsModalOpen] = useState<boolean>(false);
    const [likeSuccessCount, setLikeSuccessCount] = useState<number>(0);
    const [followSuccessCount, setFollowSuccessCount] = useState<number>(0);
    const [likeSuccessCount24h, setLikeSuccessCount24h] = useState<number>(0);
    const [followSuccessCount24h, setFollowSuccessCount24h] = useState<number>(0);

    const handleScheduleLogsModal = (open: boolean) => {
        setScheduleLogsModalOpen(open);
    };

    const _getCanSchedule = useCallback(async () => {
        const _enabled = await repository.getCanSchedule();
        setEnabled(_enabled);
    }, []);


    const _getMagicValue = async () => {
        settingStore.initialize();
    };

    const handleChange = useCallback(async (checked: boolean) => {
        try {
            setLoading(true);
            if (checked) {
                const succeed = await repository.activateSchedule();
                if (!succeed) {
                    throw new Error('스케줄러를 켜는데 실패하였습니다');
                }
                message.success('스케줄러를 켰습니다.');
            } else {
                const succeed = await repository.deactivateSchedule();
                if (!succeed) {
                    throw new Error('스케줄러를 끄는데 실패하였습니다');
                }
                message.success('스케줄러를 껐습니다.');
            }
            await _getCanSchedule();
            setTimeout(() => {
                setLoading(false);
            }, 300);
        } catch (err) {
            setLoading(false);
            // @ts-ignore
            message.error(err.message);
        } finally {
            setTimeout(() => {
                setLoading(false);
            }, 1000);
        }
    }, []);

    const getDashboardStatus = async () => {
        try {
            const dashboardStatus = await repository.getBiscuit('dashboard-status') as unknown as DashboardStatus;
            if (dashboardStatus) {
                setDashboardStatus(dashboardStatus);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const getTotalSuccessCount = async () => {
        try {
            const endDate = moment().utc().format('YYYY-MM-DD HH:mm:ss');
            const startDate1h = moment().subtract(1, 'hours').utc().format('YYYY-MM-DD HH:mm:ss');
            const startDate24h = moment().subtract(24, 'hours').utc().format('YYYY-MM-DD HH:mm:ss');

            // 1시간 좋아요 개수 조회
            const { count: like1hCount, error: like1hError } = await supabase
                .from('account_logs')
                .select('*', { count: 'exact', head: true })
                .in('task_type', ['schedule', 'like'])
                .eq('status', 'success')
                .gte('created_at', startDate1h)
                .lte('created_at', endDate);

            // 1시간 팔로우 개수 조회
            const { count: follow1hCount, error: follow1hError } = await supabase
                .from('account_logs')
                .select('*', { count: 'exact', head: true })
                .eq('task_type', 'follow')
                .eq('status', 'success')
                .gte('created_at', startDate1h)
                .lte('created_at', endDate);

            // 24시간 좋아요 개수 조회
            const { count: like24hCount, error: like24hError } = await supabase
                .from('account_logs')
                .select('*', { count: 'exact', head: true })
                .in('task_type', ['schedule', 'like'])
                .eq('status', 'success')
                .gte('created_at', startDate24h)
                .lte('created_at', endDate);

            // 24시간 팔로우 개수 조회
            const { count: follow24hCount, error: follow24hError } = await supabase
                .from('account_logs')
                .select('*', { count: 'exact', head: true })
                .eq('task_type', 'follow')
                .eq('status', 'success')
                .gte('created_at', startDate24h)
                .lte('created_at', endDate);

            if (like1hError || follow1hError || like24hError || follow24hError) {
                console.error('Error fetching success counts:', like1hError || follow1hError || like24hError || follow24hError);
                return;
            }

            setLikeSuccessCount(like1hCount ?? 0);
            setFollowSuccessCount(follow1hCount ?? 0);
            setLikeSuccessCount24h(like24hCount ?? 0);
            setFollowSuccessCount24h(follow24hCount ?? 0);
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        _getCanSchedule();
        _getMagicValue();
        getDashboardStatus();
        getTotalSuccessCount();
    }, []);

    // 5초마다 스케줄링 상태 갱신
    useEffect(() => {
        const timerId = setInterval(() => {
            _getCanSchedule();
        }, 5 * 1000);
        const timerId2 = setInterval(() => {
            _getMagicValue();
        }, 10 * 1000);
        const timerId3 = setInterval(() => {
            getDashboardStatus();
        }, 30 * 1000);
        const timerId4 = setInterval(() => {
            getTotalSuccessCount();
        }, 60 * 1000); // 1분마다 총 성공 횟수 업데이트
        return () => {
            clearInterval(timerId);
            clearInterval(timerId2);
            clearInterval(timerId3);
            clearInterval(timerId4);
        }
    }, []);

    return (
        <div>
            <Flex justifyContent='space-between'>
                <div>스케줄러 ON/OFF</div>
                <div>
                    {enabled !== undefined &&
                        <Switch
                            loading={loading}
                            checked={enabled}
                            onChange={handleChange}
                        />
                    }
                </div>
            </Flex>
            <div style={{
                padding: 10,
                background: 'white',
                borderRadius: 10,
                marginTop: 20,
                color: 'black',
            }}>
                {/* <Flex justifyContent='space-between'>
                    <div>
                        스케줄
                        <span style={{ color: '#F77737', fontWeight: 'bold', fontSize: 16, margin: '0 3px 0 6px' }}>
                            {(settingStore.magicValues?.WORKER_COUNT ?? 0) * 10}
                        </span>
                        / 회
                    </div>
                    <Link to='/setting' style={{ color: 'black' }}>
                        <button style={{ padding: 4 }}>
                            <SettingOutlined color='black' />
                        </button>
                    </Link>
                </Flex> */}
                <div>
                    <div>좋아요</div>
                    <div>
                        <span style={{ color: '#F77737', fontWeight: 'bold', fontSize: 16, margin: '0 3px 0 0' }}>
                            {likeSuccessCount.toLocaleString()}
                        </span>
                        <span style={{ marginRight: 10 }}>/ h</span>
                        <span style={{ color: '#F77737', fontWeight: 'bold', fontSize: 14, margin: '0 3px 0 0' }}>
                            {likeSuccessCount24h.toLocaleString()}
                        </span>
                        <span>/ 24h</span>
                    </div>
                </div>
                <div style={{ marginTop: 10 }}>
                    <div>팔로우</div>
                    <div>
                        <span style={{ color: '#52c41a', fontWeight: 'bold', fontSize: 16, margin: '0 3px 0 0' }}>
                            {followSuccessCount.toLocaleString()}
                        </span>
                        <span style={{ marginRight: 10 }}>/ h</span>
                        <span style={{ color: '#52c41a', fontWeight: 'bold', fontSize: 14, margin: '0 3px 0 0' }}>
                            {followSuccessCount24h.toLocaleString()}
                        </span>
                        <span>/ 24h</span>
                    </div>
                </div>
                {dashboardStatus &&
                    <>
                        <Flex
                            justifyContent='space-between'
                            style={{ marginTop: 10 }}
                        >
                            포화율
                            <Flex>
                                <span
                                    style={{
                                        color: (dashboardStatus.saturationRate ?? 1) >= 1.9 ? '#E1306C' : '#119911',
                                        fontWeight: 'bold',
                                        fontSize: 16,
                                        margin: '0 5px 0 6px',
                                    }}
                                >
                                    {Math.floor((dashboardStatus.saturationRate ?? 1) * 100)}%
                                </span>
                                <span>
                                    {settingStore.magicValues?.SCHEDULE_MAX_WORKFLOW === Interval.SCHEDULE_MAX_WORKFLOW.max && '🚀'}
                                </span>
                            </Flex>
                        </Flex>
                        <Flex
                            justifyContent='space-between'
                            style={{ marginTop: 10 }}
                        >
                            좋아요
                            <span style={{ color: 'gray', fontWeight: 'bold', fontSize: 16, margin: '0 3px 0 6px' }}>
                                {dashboardStatus.remainCnts.like.toLocaleString()}
                            </span>
                        </Flex>
                        <Flex
                            justifyContent='space-between'
                            style={{ marginTop: 10 }}
                        >
                            팔로우
                            <span style={{ color: 'gray', fontWeight: 'bold', fontSize: 16, margin: '0 3px 0 6px' }}>
                                {dashboardStatus.remainCnts.follow.toLocaleString()}
                            </span>
                        </Flex>
                        <Flex
                            justifyContent='space-between'
                            style={{ marginTop: 10 }}
                        >
                            구독팔로우
                            <span style={{ color: 'gray', fontWeight: 'bold', fontSize: 16, margin: '0 3px 0 6px' }}>
                                {dashboardStatus.subscriptionFollow.remain.toLocaleString()}
                            </span>
                        </Flex>
                        <Flex
                            justifyContent='space-between'
                            style={{ marginTop: 10 }}
                        >
                            <Button
                                onClick={() => handleScheduleLogsModal(true)}
                                style={{ width: '100%' }}
                                type='text'
                            >
                                {"노르망디 로그 >"}
                            </Button>
                        </Flex>
                    </>
                }
            </div>
            <ScheduleLogsModal
                visible={scheduleLogsModalOpen}
                onClose={() => handleScheduleLogsModal(false)}
            />
        </div>
    );
});

const Monitor = observer(() => {
    const monitors = [
        {
            link: 'https://devamongkr.grafana.net/d/fRIvzUZMz/proxy-manager?orgId=1&refresh=10s',
            text: '프록시 모니터',
        },
        {
            link: 'https://devamongkr.grafana.net/d/1yoY6tS4z/instaleader-worker?orgId=1&refresh=10s',
            text: '워커 모니터',
        },
        {
            link: 'https://devamongkr.grafana.net/d/yl1DU6n4z/operation?orgId=1&refresh=10s',
            text: '오퍼레이션 모니터',
        },
        {
            link: 'https://devamongkr.grafana.net/d/_LRxVeJ4k/scheduler?orgId=1',
            text: '스케줄러 모니터',
        },
        {
            link: 'https://devamongkr.grafana.net/d/bba78d28-7d92-4ed7-b023-d99c055fd6fb/deposit-checker?orgId=1',
            text: '입금확인 모니터',
        },
        {
            link: 'https://devamongkr.grafana.net/d/bd6048d0-ce47-45d3-880c-c39fa1d67114/autopilot?orgId=1',
            text: '파일럿 모니터',
        },
    ]

    return (
        <div style={{ color: '#ffffff' }}>
            <div style={{
                borderBottom: 'solid 1px gray',
                padding: '12px 24px',
            }}>
                <WorkableQuota />
            </div>
            <div style={{
                borderBottom: 'solid 1px gray',
                padding: '12px 24px',
            }}>
                <ScheduleMonitor />
                {/* <NicknameSetting /> */}
            </div>
            <div style={{
                borderBottom: 'solid 1px gray',
            }}>
                <Menu
                    theme='dark'
                    mode='inline'
                    onSelect={(info) => {
                        window.open(info.key, '_blank');
                    }}
                >
                    <Menu.SubMenu key={'모니터'} title={'모니터'}>
                        {monitors.map(monitor => (
                            <Menu.Item key={monitor.link}>
                                {monitor.text}
                            </Menu.Item>
                        ))}
                    </Menu.SubMenu>
                </Menu>
            </div>
        </div>
    );
});

const RequireAuth = ({ children }: { children: React.ReactElement }) => {
    const location = useLocation();

    if (authStore.token === null) {
        return (
            <Navigate
                to='/login'
                state={{ from: location }}
                replace
            />
        )
    }

    return children;
};

const App = observer(() => {
    const [collapsed, setCollapsed] = useState<boolean>(true);
    const navigate = useNavigate();
    const location = useLocation();

    const onSelectMenu = (keyPath: Array<string>) => {
        const to = keyPath.reverse().join('/');
        navigate(to);
    };

    useEffect(() => {
        if (!isMobileOnly) {
            setCollapsed(false);
        }
    }, [isMobileOnly]);

    useEffect(() => {
        if (location.pathname === '/') {
            navigate('/order-status');
        }
        if (isMobileOnly) {
            setCollapsed(true);
        }
    }, [location.pathname]);

    useEffect(() => {
        settingStore.initialize();
    }, []);

    return (
        <Layout style={{ minHeight: '100vh', boxSizing: 'border-box' }}>
            {authStore.token &&
                <Sider
                    collapsible
                    collapsedWidth={0}
                    collapsed={collapsed}
                    onCollapse={value => setCollapsed(value)}
                    style={{
                        zIndex: 100,
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        bottom: 0,
                    }}
                >
                    <div style={{
                        height: '100%',
                        overflowY: 'scroll'
                    }}>
                        <Monitor />
                        <Menu
                            theme='dark'
                            mode='inline'
                            onSelect={(info) => onSelectMenu(info.keyPath)}
                            // @ts-ignore
                            selectedKeys={[keyMapper[location.pathname]]}
                        >
                            {privatePages.map(page => {
                                if (page.subPages) {
                                    return (
                                        <Menu.SubMenu key={page.key} title={page.name}>
                                            {page.subPages.map(subPage => (
                                                <Menu.Item key={subPage.key}>
                                                    {subPage.name}
                                                </Menu.Item>
                                            ))}
                                        </Menu.SubMenu>
                                    );
                                }

                                return (
                                    <Menu.Item key={page.key}>
                                        {page.name}
                                    </Menu.Item>
                                );
                            })}
                        </Menu>
                    </div>
                </Sider>
            }
            <Layout style={{ background: 'white', paddingLeft: collapsed ? 0 : 200 }}>
                <Content style={{ background: 'white', position: 'relative', overflowX: 'scroll', height: '100vh' }}>
                    <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><Spin size="large" /></div>}>
                        <Routes>
                            {pages.map(page => (
                                <Route
                                    key={page.key}
                                    path={page.key}
                                    element={page.component}
                                />
                            ))}
                            {privatePages.map(page => {
                                if (page.subPages) {
                                    return (
                                        <Route
                                            key={page.key}
                                            path={page.key}
                                        >
                                            {page.subPages.map(subPage => (
                                                <Route
                                                    key={subPage.key}
                                                    path={subPage.key}
                                                    element={
                                                        <RequireAuth>
                                                            {subPage.component}
                                                        </RequireAuth>
                                                    }
                                                />
                                            ))}
                                        </Route>
                                    );
                                }

                                return (
                                    <Route
                                        key={page.key}
                                        path={page.key}
                                        element={
                                            <RequireAuth>
                                                {page.component}
                                            </RequireAuth>
                                        }
                                    />
                                );
                            })}
                        </Routes>
                    </Suspense>
                </Content>
            </Layout>
        </Layout>
    );
});

export default App;